import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { RESERVATION_STATUS, human } from "@/lib/statusLabels";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Check, X, Plus } from "lucide-react";

const STATUS_BADGE = {
  ACTIVE: "badge-available",
  PENDING_APPROVAL: "badge-reserved",
  DECLINED: "badge-sold",
  EXPIRED: "badge-neutral",
  CANCELLED: "badge-neutral",
  CONVERTED: "badge-available",
};

function ReserveForDealerDialog({ open, onOpenChange, onCreated }) {
  const [dealers, setDealers] = useState(null);
  const [variants, setVariants] = useState(null);
  const [dealerId, setDealerId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [variantQuery, setVariantQuery] = useState("");
  const [variant, setVariant] = useState(null);
  const [sources, setSources] = useState(null);
  const [sourceKey, setSourceKey] = useState("FLORIDA");
  const [boxes, setBoxes] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDealerId("");
    setLocationId("");
    setVariant(null);
    setVariantQuery("");
    setSources(null);
    setSourceKey("FLORIDA");
    setBoxes("");
    setNote("");
    api.get("/dealers").then((r) => setDealers(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api
      .get("/inventory", { params: { location: "FLORIDA", canonical: true } })
      .then((r) => setVariants(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [open]);

  const dealer = (dealers || []).find((d) => d.id === dealerId) || null;
  const locations = dealer?.locations || [];

  const pickDealer = (id) => {
    setDealerId(id);
    const d = (dealers || []).find((x) => x.id === id);
    const locs = d?.locations || [];
    const def = locs.find((l) => l.is_default) || (locs.length === 1 ? locs[0] : null);
    setLocationId(def ? def.id : "");
  };

  const pickVariant = async (v) => {
    setVariant(v);
    setSources(null);
    setSourceKey("FLORIDA");
    try {
      const r = await api.get("/admin/reservations/sources", { params: { variant_id: v.variant_id } });
      setSources(r.data);
      if (!(r.data.florida_available > 0) && r.data.incoming.length > 0) {
        setSourceKey(`CONTAINER:${r.data.incoming[0].container_id}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const nboxes = parseInt(boxes, 10) || 0;
  const coverage = sources?.box_coverage_sqft ?? variant?.box_coverage_sqft ?? 0;
  const totalSqft = nboxes > 0 ? Math.round(nboxes * coverage * 100) / 100 : 0;
  const selectedIncoming = sourceKey.startsWith("CONTAINER:")
    ? (sources?.incoming || []).find((i) => `CONTAINER:${i.container_id}` === sourceKey)
    : null;
  const available = sourceKey === "FLORIDA" ? sources?.florida_available ?? 0 : selectedIncoming?.available_boxes ?? 0;
  const sourceLabel = sourceKey === "FLORIDA" ? "Florida warehouse" : `Incoming — ${selectedIncoming?.name || ""}`;
  const overAvailable = !!sources && nboxes > available;
  const ready = !!dealerId && !!variant && nboxes > 0 && !!sources && sources.sellable;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post("/admin/reservations", {
        dealer_id: dealerId,
        variant_id: variant.variant_id,
        boxes: nboxes,
        location_id: locationId || null,
        container_id: sourceKey.startsWith("CONTAINER:") ? sourceKey.slice("CONTAINER:".length) : null,
        admin_note: note.trim(),
      });
      toast.success(
        r.data.status === "PENDING_APPROVAL"
          ? "Repeat hold created — pending approval in the queue"
          : `Reservation created — ${r.data.boxes} boxes held for ${r.data.hold_hours}h`
      );
      onOpenChange(false);
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const filtered = (variants || [])
    .filter((v) => `${v.label} ${v.sku || ""}`.toLowerCase().includes(variantQuery.trim().toLowerCase()))
    .slice(0, 8);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-white sm:max-w-lg" data-testid="reserve-for-dealer-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium text-charcoal">Reserve for dealer</DialogTitle>
          <DialogDescription className="text-sm text-warmgrey">
            Create a hold on behalf of a dealer. The same availability, eligibility and expiration rules apply as for dealer-created reservations.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="mt-2 space-y-5" data-testid="reserve-for-dealer-form">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Dealer</Label>
            <Select value={dealerId} onValueChange={pickDealer}>
              <SelectTrigger data-testid="rfd-dealer-select" className="h-11 bg-white">
                <SelectValue placeholder={dealers ? "Select dealer" : "Loading dealers…"} />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {(dealers || []).map((d) => (
                  <SelectItem key={d.id} value={d.id} data-testid={`rfd-dealer-option-${d.id}`}>
                    {d.dba || d.legal_name}
                    {d.dealer_code ? ` · ${d.dealer_code}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {dealer && locations.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Location</Label>
              <Select value={locationId} onValueChange={setLocationId}>
                <SelectTrigger data-testid="rfd-location-select" className="h-11 bg-white">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {locations.map((l) => (
                    <SelectItem key={l.id} value={l.id} data-testid={`rfd-location-option-${l.id}`}>
                      {l.label}
                      {l.address?.city ? ` — ${l.address.city}, ${l.address.state}` : ""}
                      {l.is_default ? " (default)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Product / variant</Label>
            {variant ? (
              <div className="flex items-center justify-between rounded-md border border-limestone bg-sand px-3 py-2" data-testid="rfd-variant-selected">
                <span className="text-sm text-charcoal">
                  {variant.label}
                  {variant.sku ? ` · ${variant.sku}` : ""}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setVariant(null);
                    setSources(null);
                  }}
                  data-testid="rfd-variant-clear"
                  className="text-xs text-warmgrey hover:text-charcoal"
                >
                  Change
                </button>
              </div>
            ) : (
              <>
                <Input
                  data-testid="rfd-variant-search"
                  value={variantQuery}
                  onChange={(e) => setVariantQuery(e.target.value)}
                  placeholder="Search by MZ code, color or format…"
                  className="h-11 bg-white focus-visible:ring-terracotta"
                />
                {variantQuery.trim() && (
                  <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-limestone p-1" data-testid="rfd-variant-results">
                    {filtered.length === 0 ? (
                      <p className="p-2 text-xs text-warmgrey">No variants match.</p>
                    ) : (
                      filtered.map((v) => (
                        <button
                          key={v.variant_id}
                          type="button"
                          onClick={() => pickVariant(v)}
                          data-testid={`rfd-variant-option-${v.variant_id}`}
                          className="flex w-full items-center justify-between gap-2 rounded px-2 py-2 text-left text-sm hover:bg-sand"
                        >
                          <span className="text-charcoal">{v.label}</span>
                          <span className="text-xs text-warmgrey">{v.available} FL available</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {sources && !sources.sellable && (
            <p className="text-sm text-status-sold" data-testid="rfd-not-sellable">
              This variant is not available for reservation.
            </p>
          )}

          {sources && sources.sellable && (
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Inventory source</Label>
              <Select value={sourceKey} onValueChange={setSourceKey}>
                <SelectTrigger data-testid="rfd-source-select" className="h-11 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="FLORIDA" data-testid="rfd-source-florida">
                    Florida warehouse — {sources.florida_available} boxes available
                  </SelectItem>
                  {sources.incoming.map((i) => (
                    <SelectItem key={i.container_id} value={`CONTAINER:${i.container_id}`} data-testid={`rfd-source-container-${i.container_id}`}>
                      Incoming · {i.name} — {i.available_boxes} boxes
                      {i.eta ? ` · ETA ${i.eta}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Boxes</Label>
            <Input
              data-testid="rfd-boxes-input"
              type="number"
              min="1"
              value={boxes}
              onChange={(e) => setBoxes(e.target.value)}
              className="h-11 bg-white focus-visible:ring-terracotta"
              placeholder="0"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Internal note (admin only — never shown to the dealer)</Label>
            <Input
              data-testid="rfd-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="h-11 bg-white focus-visible:ring-terracotta"
              placeholder="e.g. Called in — hold for project X"
            />
          </div>

          {ready && (
            <div className="rounded-md border border-limestone bg-sand p-4 text-sm" data-testid="rfd-summary">
              <p className="font-medium text-charcoal">{variant.label}</p>
              <p className="mt-1 text-warmgrey">
                {coverage} sqft/box · {nboxes} boxes = <span className="font-medium text-charcoal">{totalSqft} sqft</span>
              </p>
              <p className="mt-1 text-warmgrey">
                {sourceLabel} · {available} boxes currently eligible
              </p>
              <p className="mt-1 text-warmgrey">Held for {sources.hold_hours} hours from creation, then expires automatically.</p>
              {overAvailable && (
                <p className="mt-2 text-status-sold" data-testid="rfd-over-available">
                  Requested quantity exceeds the currently eligible amount — availability is re-checked on confirm.
                </p>
              )}
            </div>
          )}

          <Button
            type="submit"
            disabled={!ready || saving}
            data-testid="rfd-confirm-button"
            className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm reservation"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminReservations() {
  const [pending, setPending] = useState(null);
  const [all, setAll] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  const [declineTarget, setDeclineTarget] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [reserveOpen, setReserveOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/reservations/pending").then((res) => setPending(res.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get("/admin/reservations").then((res) => setAll(res.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  };

  useEffect(load, []);

  const handleApprove = async (holdHours) => {
    setSaving(true);
    try {
      await api.post(`/admin/reservations/${approveTarget.id}/approve`, { hold_hours: holdHours });
      toast.success(`Approved for ${holdHours}h — stock now reserved`);
      setApproveTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDecline = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/admin/reservations/${declineTarget.id}/decline`, { reason: declineReason });
      toast.success("Declined");
      setDeclineTarget(null);
      setDeclineReason("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const Row = ({ r, actions }) => (
    <Card className="border-border bg-white" data-testid={`admin-reservation-${r.id}`}>
      <CardContent className="flex flex-wrap items-center justify-between gap-4 p-5">
        <div>
          <p className="font-medium text-charcoal">{r.variant_label}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {r.dealer_name} · {r.boxes} boxes · {r.source === "FLORIDA" ? "Florida" : "In transit"}
            {r.is_repeat ? " · repeat" : " · first hold"}
            {r.expires_at && r.effective_status === "ACTIVE" ? ` · expires ${new Date(r.expires_at).toLocaleString()}` : ""}
          </p>
          {r.created_by_type === "ADMIN" && (
            <p className="mt-1 text-xs text-warmgrey" data-testid={`admin-created-origin-${r.id}`}>
              Created by Zelligio Admin{r.created_by_name ? ` (${r.created_by_name})` : ""} on behalf of {r.dealer_name}
            </p>
          )}
          {r.admin_note && (
            <p className="mt-1 text-xs italic text-warmgrey" data-testid={`admin-note-${r.id}`}>
              Internal note: {r.admin_note}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {r.created_by_type === "ADMIN" && (
            <span className="badge-status badge-neutral" data-testid={`admin-created-badge-${r.id}`}>
              Created by Admin
            </span>
          )}
          <span className={`badge-status ${STATUS_BADGE[r.effective_status] || "badge-neutral"}`} data-testid={`admin-reservation-status-${r.id}`}>
            {human(RESERVATION_STATUS, r.effective_status)}
          </span>
          {actions}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div data-testid="admin-reservations-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Holds</p>
          <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Reservations</h1>
        </div>
        <Button onClick={() => setReserveOpen(true)} data-testid="reserve-for-dealer-button" className="bg-terracotta text-white hover:bg-terracotta-hover">
          <Plus className="mr-1 h-4 w-4" /> Reserve for dealer
        </Button>
      </div>

      <Tabs defaultValue="queue" className="mt-10">
        <TabsList className="bg-sand">
          <TabsTrigger value="queue" data-testid="tab-approval-queue">
            Approval queue{pending?.length ? ` (${pending.length})` : ""}
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all-reservations">All reservations</TabsTrigger>
        </TabsList>

        <TabsContent value="queue" className="mt-6">
          {!pending ? (
            <Loader2 className="h-6 w-6 animate-spin text-terracotta" />
          ) : pending.length === 0 ? (
            <Card className="border-border bg-white"><CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="queue-empty">No repeat reservations awaiting approval.</CardContent></Card>
          ) : (
            <div className="space-y-3">
              {pending.map((r) => (
                <Row
                  key={r.id}
                  r={r}
                  actions={
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => setApproveTarget(r)} data-testid={`approve-${r.id}`} className="bg-terracotta text-white hover:bg-terracotta-hover">
                        <Check className="mr-1 h-3.5 w-3.5" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setDeclineTarget(r)} data-testid={`decline-${r.id}`} className="border-limestone text-status-sold hover:bg-sand">
                        <X className="mr-1 h-3.5 w-3.5" /> Decline
                      </Button>
                    </div>
                  }
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="all" className="mt-6">
          {!all ? (
            <Loader2 className="h-6 w-6 animate-spin text-terracotta" />
          ) : all.length === 0 ? (
            <Card className="border-border bg-white"><CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="all-empty">No reservations yet.</CardContent></Card>
          ) : (
            <div className="space-y-3">{all.map((r) => <Row key={r.id} r={r} actions={null} />)}</div>
          )}
        </TabsContent>
      </Tabs>

      <ReserveForDealerDialog open={reserveOpen} onOpenChange={setReserveOpen} onCreated={load} />

      <Dialog open={!!approveTarget} onOpenChange={(o) => !o && setApproveTarget(null)}>
        <DialogContent className="border-border bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Approve repeat hold</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              {approveTarget?.variant_label} · {approveTarget?.boxes} boxes · {approveTarget?.dealer_name}. Stock is reserved from this moment — choose the hold duration.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 grid grid-cols-3 gap-3" data-testid="approve-duration-options">
            {[24, 48, 72].map((h) => (
              <Button
                key={h}
                onClick={() => handleApprove(h)}
                disabled={saving}
                data-testid={`approve-${h}h`}
                className="h-14 bg-terracotta text-white hover:bg-terracotta-hover"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : `${h}h`}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!declineTarget} onOpenChange={(o) => !o && setDeclineTarget(null)}>
        <DialogContent className="border-border bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Decline repeat hold</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">The dealer is notified. No stock was held.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleDecline} className="mt-2 space-y-5" data-testid="decline-form">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Reason</Label>
              <Input data-testid="decline-reason-input" value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} placeholder="Optional note to dealer" className="h-11 bg-white focus-visible:ring-terracotta" />
            </div>
            <Button type="submit" disabled={saving} data-testid="decline-submit" className="h-11 w-full bg-status-sold text-white hover:bg-status-sold/90">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Decline reservation"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
